import { UUID } from "@bitory-gmbh/nidavellir";
import { Filter } from "@/shared/lib/filter/filter";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { backendApi } from "@/services/backend/api";
import { PricingByArea } from "../types/pricing-by-area";
import { updateCache } from "@/services/backend/_lib/update-cache";

const pricingByAreaApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltPricingByAreaPut: build.mutation<PricingByArea, PricingByArea>({
      query: (request) => ({
        url: "/rlt/pricing-by-area",
        method: "PUT",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltPricingByArea", id: request.id },
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltConfirmation",
      ],
    }),
    rltPricingByAreaShow: build.query<PricingByArea, ShowRequest>({
      query: (request) => ({
        url: "/rlt/pricing-by-area",
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltPricingByArea", id: result.id }] : [],
    }),
    rltPricingByAreaList: build.query<List, Partial<ListRequest>>({
      query: (request) => ({
        url: "/rlt/pricing-by-area/list",
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "RltPricingByArea"),
    }),
    rltPricingByAreaUpdateUseCalculation: build.mutation<
      PricingByArea,
      UpdateUseCalculationRequest
    >({
      query: (request) => ({
        url: "/rlt/pricing-by-area/use-calculation",
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltPricingByArea", id: request.id },
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltConfirmation",
      ],
    }),
    rltPricingByAreaDelete: build.mutation<void, DeleteRequest>({
      query: (request) => ({
        url: "/rlt/pricing-by-area",
        method: "DELETE",
        params: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltPricingByArea", id: request.id },
        "RltPricingByArea",
      ],
    }),
  }),
});

export const {
  useRltPricingByAreaPutMutation,
  useRltPricingByAreaShowQuery,
  useRltPricingByAreaListQuery,
  useRltPricingByAreaUpdateUseCalculationMutation,
  useRltPricingByAreaDeleteMutation,
} = pricingByAreaApi;

export const updateRltPricingByAreaCache = updateCache(
  pricingByAreaApi,
  "rltPricingByAreaShow",
);

export interface ShowRequest {
  id: UUID;
}

export interface ListRequest {
  company?: Filter<UUID>;
  branch?: Filter<UUID>;
  limit?: number;
  offset?: number;
}

export interface List {
  items: PricingByArea[];
  count: number;
}

export interface UpdateUseCalculationRequest {
  id: UUID;
  useCalculation: boolean;
}

export interface DeleteRequest {
  id: UUID;
}

// Optimistically patch the rltPricingByAreaShow cache entry.
// Dispatch this action for immediate UI updates before the
// debounced mutations fire.
export const updatePricingByAreaCache = (data: PricingByArea) =>
  pricingByAreaApi.util.updateQueryData(
    "rltPricingByAreaShow",
    { id: data.id },
    () => data,
  );
